import React, { type FC, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import type { EdgeProps, NodeProps } from '@xyflow/react'
import { Flow } from 'widgets/Flow'
import { SimpleFlowEdge } from 'widgets/Flow/ui/SimpleFlowEdge/SimpleFlowEdge'
import { RingNode } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/Ring'
import { CustomEdgeWithLabel } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/CustomEdge/CustomEdgeWithLabel'
import { type ISmartCallFlowActionType } from 'pages/settings/pages/inboxes/pages/id/pages/settings/calling/smart-call-flow/ui/SmartCallFlowBuilder/ui/actions/type'
import { getNodesByChangesMap } from '../utils/getNodesByChangesMap'
import { AddButtonNode } from './actions/AddButton/ui/AddButtonNode/AddButtonNode'
import {
  CallEndsNode,
  CallQueueNode,
  ForwardCallNode,
  IncomingCallNode,
  IVRNode,
  PlayAudioNode,
  SendSMSNode,
  SendToVoicemailNode,
  AICallingAgentNode,
} from './actions'
import { CustomEdge } from './CustomEdge/CustomEdge'
import { SelectedAction } from './SelectedAction/SelectedAction'
import { useSmartCallFlowBuilderContext } from '../context'

type IExistNodes = Extract<
  ISmartCallFlowActionType,
  | 'incomingCall'
  | 'callingFlow'
  | 'sendVoicemail'
  | 'playSound'
  | 'forwardCall'
  | 'callEnds'
  | 'callQueue'
  | 'sendMessage'
  | 'ivrPhoneTree'
  | 'aiCallingAgent'
  | 'addButton'
>

export type ISmartCallFlowBuilderUiProps = {
  viewOnly?: boolean
  disabled?: boolean
  fitViewOnChange?: boolean
}

export type ISmartCallNodeProps = NodeProps & { viewOnly?: boolean; disabled?: boolean }

export const SmartCallFlowBuilderUi = observer(
  ({ viewOnly, disabled, fitViewOnChange }: ISmartCallFlowBuilderUiProps) => {
    const { actions, getActionById, endActions } = useSmartCallFlowBuilderContext()

    const edgeTypes = useMemo(
      () => ({
        simple: (props: EdgeProps) => <SimpleFlowEdge disabled={disabled} {...props} />,
        customEdge: (props: EdgeProps) => <CustomEdge disabled={disabled} {...props} />,
        customEdgeWithLabel: (props: EdgeProps) => (
          <CustomEdgeWithLabel disabled={disabled} {...props} />
        ),
      }),
      [disabled]
    )

    const nodeTypes: Record<IExistNodes, FC<NodeProps>> = useMemo(
      () => ({
        incomingCall: (props: NodeProps) => (
          <IncomingCallNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        sendVoicemail: (props: NodeProps) => (
          <SendToVoicemailNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        callingFlow: (props: NodeProps) => (
          <RingNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        playSound: (props: NodeProps) => (
          <PlayAudioNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        forwardCall: (props: NodeProps) => (
          <ForwardCallNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        callEnds: (props: NodeProps) => (
          <CallEndsNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        callQueue: (props: NodeProps) => (
          <CallQueueNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        sendMessage: (props: NodeProps) => (
          <SendSMSNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        ivrPhoneTree: (props: NodeProps) => (
          <IVRNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        aiCallingAgent: (props: NodeProps) => (
          <AICallingAgentNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
        addButton: (props: NodeProps) => (
          <AddButtonNode viewOnly={viewOnly} disabled={disabled} {...props} />
        ),
      }),
      [viewOnly, disabled]
    )

    return (
      <Flow
        actions={actions}
        endActions={endActions}
        getActionById={getActionById}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        getNodesByChangesMap={getNodesByChangesMap}
        selectedAction={<SelectedAction />}
        viewOnly={viewOnly}
        fitViewOnChange={fitViewOnChange}
      />
    )
  }
)
